import { getIntervalMillisForTimeBucket, timeBuckets, timeBucketToBucketCategory, } from '@thoughtspot/date-format-util';
import { isSpecialInfinityValue, isSpecialMinusInfinityValue, isSpecialNaNValue, isSpecialValue, } from '@thoughtspot/special-data-value-format';
import { convertValueFromBackend, getColumnSortPriorityOrder, getCustomCalendarMapForColumn, getEffectiveDataType, getId, getTimeBucket, isAttribute, isDateTimeColumn, isEffectivelyNonNumeric, isEffectivelyNumeric, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getDataRowIndex, getTooltipColumns } from '../core/axis-config-util';
import { formatCompositeColumnValue, getChartType, getColorBySerieName, getDistinctNamesForColumns, getLegendColumns, getSeriesColorsMap, getVisibleSeriesIds, getXAxisColumns, getYAxisColumns, hasAnySortedMeasure, hasAnySortedMeasureNotOnAxis, isAscendingSort, isCustomCalendarTimeSeries, isPrimarySortOnXAxisColumns, isSorted, isTimeSeries as getIsTimeSeries, isUserSorted, isXAxisOrdinalBased as getIsXAxisOrdinalBased, isYAxisOrdinalBased as getIsYAxisOrdinalBased, xAxisColumnHasCustomSort, xAxisColumnHasSorting, yAxisColumnHasSorting, } from '../core/chart-model-utils';
import { configOptions, getStackedValuesColumn, } from '../core/chart-type-specification-service';
import { ErrorTypes } from '../core/chart-data-validation';
import { formattingConstants } from '../core/chart-ui-utils';
import { getName } from '../core/column-name-util';
import { getColumnsUsedInConditionalFormatting } from '../core/conditional-formatting-utils';
import { customSortOnSeriesData, filterColumnIdsForSeries, getCustomOrderInSequenceWithoutColumnId, getVizColumnsSortOrder, isCustomSortSupportedForChartType, sortWithCustomOrder, } from '../core/custom-sort-utils';
import { convertNullToShiftedDateTime, getColumnConfig, getColumnDataValues, getDataArray, getDataRowIndicesAllowingNullsOrZeroes, getDisplayValue, getTooltipConfig, ShowNullAsType, updateNullToZero, } from '../data/data-util';
import { getEnvFlags } from '../env';
import { ChartType, } from '../types';
import { computeAxisInfinityPlaceholderValue, } from '../utils';
import { createLogger } from '../utils/logger';
import { configureSeriesVisibility } from './highchart-series-util';
let logger;
function axisColumnValuesComparator(descending, valuesA, valuesB, hashKeyA, hashKeyB) {
    if (!valuesA && !valuesB) {
        return 0;
    }
    if (!valuesA) {
        return 1;
    }
    if (!valuesB) {
        return -1;
    }
    if (valuesA.length !== valuesB.length) {
        logger.warn('mismatch in values array length', valuesA.length, valuesB.length, valuesA, valuesB);
        return 0;
    }
    for (let i = 0; i < valuesA.length; i++) {
        const valueA = valuesA[i].value;
        const valueB = valuesB[i].value;
        const aIsInfinity = isSpecialInfinityValue(valueA);
        const bIsInfinity = isSpecialInfinityValue(valueB);
        if (aIsInfinity && bIsInfinity) {
            continue;
        }
        if (aIsInfinity && !bIsInfinity) {
            return descending ? -1 : 1;
        }
        if (!aIsInfinity && bIsInfinity) {
            return descending ? 1 : -1;
        }
        if (valueA === null || valueB === null) {
            if (valueA === null && valueB === null) {
                continue;
            }
            if (valueA === null) {
                return descending ? 1 : -1;
            }
            return descending ? -1 : 1;
        }
        if (valueA > valueB) {
            return descending ? -1 : 1;
        }
        if (valueA < valueB) {
            return descending ? 1 : -1;
        }
    }
    return 0;
}
function yAxisColumnValuesComparator(descending, valuesA, valuesB, hashKeyA, hashKeyB) {
    if (!valuesA && !valuesB) {
        return 0;
    }
    if (!valuesA) {
        return 1;
    }
    if (!valuesB) {
        return -1;
    }
    if (valuesA.length !== valuesB.length) {
        logger.warn('mismatch in values array length', valuesA.length, valuesB.length, valuesA, valuesB);
        return 0;
    }
    const valueA = valuesA.value;
    const valueB = valuesB.value;
    const aIsInfinity = isSpecialInfinityValue(valueA);
    const bIsInfinity = isSpecialInfinityValue(valueB);
    if (aIsInfinity && bIsInfinity) {
        return 0;
    }
    if (aIsInfinity && !bIsInfinity) {
        return descending ? -1 : 1;
    }
    if (!aIsInfinity && bIsInfinity) {
        return descending ? 1 : -1;
    }
    if (valueA === null || valueB === null) {
        if (valueA === null && valueB === null) {
            return 0;
        }
        if (valueA === null) {
            return descending ? 1 : -1;
        }
        return descending ? -1 : 1;
    }
    if (valueA > valueB) {
        return descending ? -1 : 1;
    }
    if (valueA < valueB) {
        return descending ? 1 : -1;
    }
    return 0;
}
const clearObject = function (object) {
    _.each(_.keys(object), key => {
        delete object[key];
    });
};
function dataRowIndexGetter(column) {
    return getDataRowIndex(column);
}
function getDataRowIndexToColumnMap(columns) {
    return columns.reduce((dataRowIndexToColumnMap, column) => {
        dataRowIndexToColumnMap[getDataRowIndex(column)] = column;
        return dataRowIndexToColumnMap;
    }, {});
}
function sortUniqueValues(uniqueValues, uniqueValuesHash, comparator) {
    if (!uniqueValues) {
        return;
    }
    const hashKeys = _.keys(uniqueValuesHash);
    hashKeys.sort((hashKeyA, hashKeyB) => {
        const valuesIndexA = uniqueValuesHash[hashKeyA];
        const valuesIndexB = uniqueValuesHash[hashKeyB];
        const valuesA = uniqueValues[valuesIndexA];
        const valuesB = uniqueValues[valuesIndexB];
        return comparator(valuesA, valuesB, hashKeyA, hashKeyB);
    });
    const oldUniqueValues = _.cloneDeep(uniqueValues);
    const oldUniqueValuesHash = _.cloneDeep(uniqueValuesHash);
    uniqueValues.length = 0;
    clearObject(uniqueValuesHash);
    _.forEach(hashKeys, (hashKey, hashKeyIndex) => {
        uniqueValuesHash[hashKey] = hashKeyIndex;
        const oldHashKeyIndex = oldUniqueValuesHash[hashKey];
        uniqueValues[hashKeyIndex] = oldUniqueValues[oldHashKeyIndex];
    });
}
export function getTooltipDataContent(tooltipColumns, chartModel, dataModel, dataRow, pos) {
    const tooltipDataContent = {};
    tooltipColumns.forEach((tooltipColumn) => {
        const { id, dataRowIndex } = tooltipColumn;
        let value = dataRow[dataRowIndex];
        if (_.isUndefined(value)) {
            const colValues = getColumnDataValues(id, chartModel);
            value = _.isArray(colValues) ? colValues[pos] : value;
        }
        tooltipDataContent[id] = {
            column: getColumnConfig(tooltipColumn, chartModel, dataModel),
            dataValue: value,
        };
    });
    return tooltipDataContent;
}
const axisColumnValuesComparatorAscending = axisColumnValuesComparator.bind(null, false);
const axisColumnValuesComparatorDescending = axisColumnValuesComparator.bind(null, true);
const yAxisColumnValuesComparatorAscending = yAxisColumnValuesComparator.bind(null, false);
export class ChartDataModel {
}
const baseQueryIndex = 0;
function updateValueRange(val, point, valueRange, infinityValuePoints) {
    if (isSpecialValue(val)) {
        if (isSpecialInfinityValue(val) || isSpecialMinusInfinityValue(val)) {
            infinityValuePoints.push(point);
        }
        else {
            return false;
        }
    }
    else {
        valueRange.min = Math.min(valueRange.min, val);
        valueRange.max = Math.max(valueRange.max, val);
    }
    return true;
}
export class HighchartDataModel extends ChartDataModel {
    constructor(chartModel, data, queryDefinitions, options, chartOptions, getEventMap) {
        super();
        this.chartModel = chartModel;
        this.data = data;
        this.queryDefinitions = queryDefinitions;
        this.options = options;
        this._maxColumnValues = {};
        this._minColumnValues = {};
        this.dataArray = getDataArray(data);
        this.data = getDataArray(data);
        this.displayToCustomCalendarValueMap = {};
        this._longestSerieNames = [];
        logger = createLogger('highchart-data-model');
        this.init(chartModel);
        this.processData(chartModel);
    }
    get longestSerieNames() {
        return this._longestSerieNames;
    }
    getDisplayCustomCalendarMapForColumn(column) {
        return getCustomCalendarMapForColumn(column, this.displayToCustomCalendarValueMap);
    }
    getChartPointFromDataPoint(xVal, yVal, dataRow, isXAxisOrdinalBased, isYAxisOrdinalBased, categoryColumn, radialColumn, xValueRange, yValuesRange, infinityValuePointsX, infinityValuePointsY, xRawValues, yRawValues, legendRawValues, tooltipDataContent) {
        if (xVal === undefined || yVal === undefined) {
            return null;
        }
        if (isSpecialNaNValue(xVal)) {
            xVal = null;
        }
        if (isSpecialNaNValue(yVal)) {
            yVal = null;
        }
        if (xVal === null) {
            if (yVal === null) {
                this._hasError = true;
            }
            if (!getEnvFlags().isHandleMissingValuesEnabled) {
                return null;
            }
        }
        this._xAxisHasNonNullValue = true;
        const point = {
            x: xVal,
            y: yVal,
            xRawValues,
            yRawValues,
            colorRawValues: legendRawValues,
            tooltipDataContent,
        };
        let validPoint;
        if (!isXAxisOrdinalBased) {
            if (!xValueRange) {
                logger.error('xValueRange must be provided when x axis is a measure');
                return null;
            }
            if (!infinityValuePointsX) {
                logger.error('infinityValuePointsX must be provided when x axis is a measure');
                return null;
            }
            if (xVal === null && getEnvFlags().isHandleMissingValuesEnabled) {
                validPoint = true;
            }
            else {
                validPoint = updateValueRange(xVal, point, xValueRange, infinityValuePointsX);
            }
            if (!validPoint) {
                return null;
            }
        }
        if (!isYAxisOrdinalBased) {
            validPoint = updateValueRange(yVal, point, yValuesRange, infinityValuePointsY);
            if (!validPoint) {
                return null;
            }
        }
        if (categoryColumn) {
            point.categoryName = dataRow[getDataRowIndex(categoryColumn)];
        }
        if (radialColumn) {
            const radialDataRowIndex = getDataRowIndex(radialColumn);
            const minMaxValuePath = `${baseQueryIndex}.${radialDataRowIndex}`;
            const zVal = dataRow[radialDataRowIndex];
            point.z = zVal;
            const calculatedMinRadialValue = _.get(this._minColumnValues, minMaxValuePath);
            const calculatedMaxRadialValue = _.get(this._maxColumnValues, minMaxValuePath);
            const minRadialValue = _.isUndefined(calculatedMinRadialValue)
                ? Number.POSITIVE_INFINITY
                : calculatedMinRadialValue;
            const maxRadialValue = _.isUndefined(calculatedMaxRadialValue)
                ? Number.NEGATIVE_INFINITY
                : calculatedMaxRadialValue;
            if (isSpecialInfinityValue(point.z)) {
                this._columnHasInfinityValues[radialDataRowIndex] = true;
                this._radialInfinityPoints.push(point);
            }
            else if (isSpecialMinusInfinityValue(zVal)) {
                this._columnHasMinusInfinityValues[radialDataRowIndex] = true;
                this._radialInfinityPoints.push(point);
            }
            else if (!_.isNaN(Number(zVal))) {
                _.set(this._minColumnValues, minMaxValuePath, Math.min(zVal, minRadialValue));
                _.set(this._maxColumnValues, minMaxValuePath, Math.max(zVal, maxRadialValue));
            }
        }
        return point;
    }
    getMinYValue(queryIndex, dataRowIndex) {
        if (!!this.minColumnValues && dataRowIndex >= 0) {
            const minValue = _.get(this.minColumnValues, `${queryIndex}.${dataRowIndex}`);
            return _.isNaN(Number(minValue))
                ? Number.NEGATIVE_INFINITY
                : minValue;
        }
        return Number.NEGATIVE_INFINITY;
    }
    getMaxYValue(queryIndex, dataRowIndex) {
        if (!!this.maxColumnValues && dataRowIndex >= 0) {
            const maxValue = _.get(this.maxColumnValues, `${queryIndex}.${dataRowIndex}`);
            return _.isNaN(Number(maxValue))
                ? Number.POSITIVE_INFINITY
                : maxValue;
        }
        return Number.POSITIVE_INFINITY;
    }
    init(chartModel) {
        this._shouldPlotEachYAsSeries = this.shouldPlotEachYAsSeries();
        this._shouldNormalizeSeriesNames = this.shouldNormalizeSeriesNames(chartModel);
        this._columnNegativeInfinityPlaceholder = [];
        this._columnInfinityPlaceholder = [];
    }
    shouldPlotEachYAsSeries() {
        return this.queryDefinitions[0].yAxisColumns.length > 1;
    }
    shouldNormalizeSeriesNames(chartModel) {
        let seriesNamesNeedNormalization = !this._shouldPlotEachYAsSeries &&
            getLegendColumns(chartModel).length > 0;
        if (seriesNamesNeedNormalization) {
            const noLegendColumnIsDate = getLegendColumns(chartModel).every(col => {
                return getEffectiveDataType(col) !== 'DATE';
            });
            seriesNamesNeedNormalization = !noLegendColumnIsDate;
        }
        return seriesNamesNeedNormalization;
    }
    getNormalizedSeriesName(seriesName) {
        if (!this._shouldNormalizeSeriesNames) {
            return seriesName;
        }
        if (!seriesName || typeof seriesName !== 'string') {
            return seriesName;
        }
        let normalizedName = '';
        let commaCount = 0;
        [...seriesName].forEach(ch => {
            if (ch === ',') {
                commaCount++;
                if (commaCount % 2 === 1) {
                    return;
                }
            }
            normalizedName += ch;
        });
        return normalizedName;
    }
    isSeriesVisible(chartModel, seriesName) {
        const visibleSeriesIds = getVisibleSeriesIds(chartModel);
        if (!visibleSeriesIds || !visibleSeriesIds.length) {
            return true;
        }
        const normalizedSeriesName = this.getNormalizedSeriesName(seriesName);
        return visibleSeriesIds.indexOf(normalizedSeriesName) >= 0;
    }
    processData(chartModel) {
        var _a, _b, _c, _d, _e, _f;
        this._columnHasInfinityValues = [];
        this._columnHasMinusInfinityValues = [];
        this._radialInfinityPoints = [];
        this._maxColumnValues = {};
        this._minColumnValues = {};
        const isEnhancedCfEnabled = getEnvFlags().isEnhancedCfEnabled;
        const isTooltipCustomizationEnabled = getEnvFlags()
            .isTooltipCustomizationEnabled;
        const baseQueryDefinition = this.getQueryDefinitions()[baseQueryIndex];
        const tooltipColumnIds = isTooltipCustomizationEnabled
            ? ((_a = getTooltipConfig(chartModel)) === null || _a === void 0 ? void 0 : _a.columnIds) || []
            : [];
        const conditionalFormattingColumnIds = isEnhancedCfEnabled
            ? getColumnsUsedInConditionalFormatting(chartModel)
            : [];
        const tooltipColumns = getTooltipColumns(this.getQueryDefinitions(), [...tooltipColumnIds, ...conditionalFormattingColumnIds], chartModel.columns, baseQueryIndex);
        const legendColumnDataRowIndexToColumns = getDataRowIndexToColumnMap(baseQueryDefinition.legendColumns);
        const xAxisColumnDataRowIndexToColumns = getDataRowIndexToColumnMap(baseQueryDefinition.xAxisColumns);
        const yAxisColumnDataRowIndexToColumns = getDataRowIndexToColumnMap(baseQueryDefinition.yAxisColumns);
        const tooltipColumnDataRowIndexToColumns = getDataRowIndexToColumnMap(tooltipColumns);
        const allIncludedColumns = this.getQueryDefinitions()[0].getColumnsInDataOrder();
        this.removeNulls(chartModel);
        if (getEnvFlags().isHandleMissingValuesEnabled) {
            this.dataArray = updateNullToZero(this.getDataArray(), allIncludedColumns, chartModel, getEnvFlags().isHandleMissingValuesEnabled, baseQueryIndex);
        }
        const isSortV2Enabled = getEnvFlags().isSortV2Enabled;
        const dataArray = this.getDataArray();
        const baseQueryData = dataArray[baseQueryIndex];
        const data = baseQueryData.getData();
        if (data.length === 0) {
            this._hasNoData = true;
            return;
        }
        this.normalizeBackendData(tooltipColumns);
        const isLineStackedChart = getChartType(chartModel) === ChartType.LineStackedColumn;
        const isParetoChart = getChartType(chartModel) === ChartType.Pareto;
        const isCustomCalendarTimeSeriesChart = isCustomCalendarTimeSeries(chartModel);
        let series = {};
        const categoryColumn = baseQueryDefinition.categoryColumns[0];
        const radialColumn = !isLineStackedChart && baseQueryDefinition.radialColumn;
        const uniqueXAxisValues = [];
        const uniqueXAxisValuesHash = {};
        const uniqueYAxisValues = [];
        const uniqueYAxisValuesHash = {};
        let uniqueLegendValues = [];
        const uniqueLegendValuesHash = {};
        const columnValueRanges = {};
        const infinityValuePointForColumn = {};
        let customSortOrder = null;
        if (getEnvFlags().isCustomSortingEnabled &&
            isCustomSortSupportedForChartType(getChartType(chartModel))) {
            customSortOrder = getVizColumnsSortOrder(chartModel.columns, chartModel.sortInfo, chartModel.data[0].columnDataLite);
        }
        const columnIdsInSequenceWithData = ((_c = (_b = chartModel.data) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.columnDataLite.map(column => column.columnId)) || [];
        const sortOrder = getColumnSortPriorityOrder(chartModel.sortInfo, columnIdsInSequenceWithData, getEnvFlags().isSortV2Enabled
            ? chartModel.config.axisConfig[0].x
            : null);
        const seriesNameToId = {};
        const xAxisColumns = baseQueryDefinition.xAxisColumns;
        const yAxisColumns = baseQueryDefinition.yAxisColumns;
        const plotEachYAsSeries = yAxisColumns.length > 1;
        this.distinctYAxisColumnNames =
            getDistinctNamesForColumns(getYAxisColumns(chartModel)) || [];
        if (plotEachYAsSeries) {
            if (getLegendColumns(chartModel).length > 0) {
                logger.error("multiple y-axes and legends can't be supported at the same time");
                throw new Error(ErrorTypes.FAILED_TO_RENDER_VIZ);
            }
            yAxisColumns.forEach((column, index) => {
                const seriesName = this.distinctYAxisColumnNames[index];
                series[seriesName] = [];
            });
        }
        [xAxisColumns, yAxisColumns].forEach(function (columns) {
            columns.forEach(column => {
                const dataRowIndex = getDataRowIndex(column);
                infinityValuePointForColumn[dataRowIndex] = [];
                columnValueRanges[dataRowIndex] = {
                    min: Number.POSITIVE_INFINITY,
                    max: Number.NEGATIVE_INFINITY,
                };
            });
        });
        const orderedXHashesForLegendHash = {};
        const stackedValueColumn = getStackedValuesColumn(getChartType(chartModel), this.getQueryDefinitions());
        const hasStackedDataInChart = !!stackedValueColumn;
        const xValueToStackValue = {};
        const xValueToYValue = {};
        if (getEnvFlags().isCustomSortingEnabled &&
            isCustomSortSupportedForChartType(getChartType(chartModel))) {
            sortWithCustomOrder(data, getCustomOrderInSequenceWithoutColumnId(columnIdsInSequenceWithData, customSortOrder), sortOrder);
        }
        data.forEach(dataRow => {
            const legendValues = [];
            const xAxisValues = [];
            const yAxisValues = [];
            dataRow.forEach((dataValue, dataRowIndex) => {
                let bucket = null;
                let columnEffectiveId = null;
                if (legendColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    bucket = legendValues;
                    columnEffectiveId = getId(legendColumnDataRowIndexToColumns[dataRowIndex]);
                }
                else if (xAxisColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    bucket = xAxisValues;
                    columnEffectiveId = getId(xAxisColumnDataRowIndexToColumns[dataRowIndex]);
                }
                else if (yAxisColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    bucket = yAxisValues;
                    columnEffectiveId = getId(yAxisColumnDataRowIndexToColumns[dataRowIndex]);
                }
                if (bucket) {
                    bucket.push({
                        dataRowIndex,
                        columnEffectiveId,
                        value: dataValue,
                    });
                }
            });
            const xAxisValueHashKey = xAxisValues
                .map(valueObject => {
                return getDisplayValue(valueObject.value, this.options.getTranslatedText);
            })
                .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
            if (!uniqueXAxisValuesHash.hasOwnProperty(xAxisValueHashKey)) {
                uniqueXAxisValuesHash[xAxisValueHashKey] =
                    uniqueXAxisValues.length;
                uniqueXAxisValues.push(xAxisValues);
            }
            const legendValueHashKey = legendValues
                .map(valueObject => {
                return getDisplayValue(valueObject.value, this.options.getTranslatedText);
            })
                .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
            if (!uniqueLegendValuesHash.hasOwnProperty(legendValueHashKey)) {
                uniqueLegendValuesHash[legendValueHashKey] =
                    uniqueLegendValues.length;
                uniqueLegendValues.push(legendValues);
            }
            if (!orderedXHashesForLegendHash.hasOwnProperty(legendValueHashKey)) {
                orderedXHashesForLegendHash[legendValueHashKey] = [];
            }
            orderedXHashesForLegendHash[legendValueHashKey].push(xAxisValueHashKey);
            yAxisValues.forEach(valueObject => {
                const idx = valueObject.dataRowIndex;
                if (!uniqueYAxisValuesHash[idx]) {
                    uniqueYAxisValuesHash[idx] = {};
                    uniqueYAxisValues[idx] = [];
                }
                if (!uniqueYAxisValuesHash[idx].hasOwnProperty(valueObject.value)) {
                    uniqueYAxisValuesHash[idx][valueObject.value] =
                        uniqueYAxisValues[idx].length;
                    uniqueYAxisValues[idx].push(valueObject);
                }
                if (hasStackedDataInChart &&
                    valueObject.columnEffectiveId ===
                        getId(stackedValueColumn) &&
                    this.isSeriesVisible(chartModel, legendValueHashKey)) {
                    xValueToStackValue[xAxisValueHashKey] =
                        xValueToStackValue[xAxisValueHashKey] || 0;
                    xValueToStackValue[xAxisValueHashKey] += valueObject.value;
                }
                if (isParetoChart) {
                    xValueToYValue[xAxisValueHashKey] = valueObject.value;
                }
            });
        });
        if (!hasAnySortedMeasureNotOnAxis(this.chartModel)) {
            sortUniqueValues(uniqueLegendValues, uniqueLegendValuesHash, axisColumnValuesComparatorAscending);
        }
        const xAxisColumnsHaveAttributes = xAxisColumns.some(column => isAttribute(column));
        const yAxisColumnsAreAttributes = yAxisColumns.every(column => isAttribute(column));
        if (yAxisColumnsAreAttributes && xAxisColumnsHaveAttributes) {
            if (!yAxisColumnHasSorting(this.chartModel)) {
                yAxisColumns.forEach(column => {
                    const idx = getDataRowIndex(column);
                    sortUniqueValues(uniqueYAxisValues[idx], uniqueYAxisValuesHash[idx], yAxisColumnValuesComparatorAscending);
                });
            }
        }
        if (!hasAnySortedMeasure(chartModel) &&
            (!xAxisColumnHasCustomSort(chartModel, xAxisColumns) ||
                isParetoChart) &&
            (!yAxisColumnHasSorting(chartModel) ||
                (yAxisColumnsAreAttributes &&
                    xAxisColumnsHaveAttributes &&
                    !xAxisColumnHasSorting(chartModel)))) {
            let xValueComparator;
            if (isParetoChart) {
                xValueComparator = ((uniqueValuesMap, x1, x2, key1, key2) => {
                    return uniqueValuesMap[key2] - uniqueValuesMap[key1];
                }).bind(void 0, xValueToYValue);
            }
            else {
                const xAxisDescendingSorted = getXAxisColumns(chartModel).some(col => {
                    return (isSorted(chartModel, col) &&
                        !isAscendingSort(chartModel, col, undefined, true));
                });
                xValueComparator = xAxisDescendingSorted
                    ? axisColumnValuesComparatorDescending
                    : axisColumnValuesComparatorAscending;
            }
            sortUniqueValues(uniqueXAxisValues, uniqueXAxisValuesHash, xValueComparator);
        }
        if (hasStackedDataInChart &&
            isUserSorted(chartModel, stackedValueColumn)) {
            const comparator = (kvMap, isAscending, x1, x2, key1, key2) => {
                return isAscending
                    ? kvMap[key1] - kvMap[key2]
                    : kvMap[key2] - kvMap[key1];
            };
            const sortUniqueComparator = comparator.bind(void 0, xValueToStackValue, isAscendingSort(chartModel, stackedValueColumn));
            sortUniqueValues(uniqueXAxisValues, uniqueXAxisValuesHash, sortUniqueComparator);
        }
        const isXAxisOrdinalBased = getIsXAxisOrdinalBased(chartModel);
        const isYAxisOrdinalBased = getIsYAxisOrdinalBased(chartModel);
        data.forEach((dataRow, dataIndex) => {
            const legendValues = [];
            const xAxisValues = [];
            const yAxisValues = [];
            const tooltipDataContent = {};
            dataRow.forEach((dataValue, dataRowIndex) => {
                if (legendColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    legendValues.push(getDisplayValue(dataValue, this.options.getTranslatedText));
                }
                else if (xAxisColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    xAxisValues.push(dataValue);
                }
                else if (yAxisColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    yAxisValues.push(dataValue);
                }
                if (tooltipColumnDataRowIndexToColumns.hasOwnProperty(dataRowIndex)) {
                    const column = tooltipColumnDataRowIndexToColumns[dataRowIndex];
                    let value = dataValue;
                    if (isLineStackedChart && _.isUndefined(value)) {
                        const colValues = getColumnDataValues(column.id, chartModel);
                        value = _.isArray(colValues)
                            ? colValues[dataIndex]
                            : value;
                    }
                    tooltipDataContent[column.id] = {
                        column: getColumnConfig(column, chartModel, this),
                        dataValue: value,
                    };
                }
            });
            let xValue = null;
            let xValueHash = null;
            let xValueRange = null;
            let infinityValuePointsX = null;
            let xRawValues = [];
            let yRawValues = [];
            if (!isXAxisOrdinalBased) {
                xValue = xAxisValues[0];
                xRawValues = [xValue];
                xValueHash = getDisplayValue(xAxisValues[0], this.options.getTranslatedText);
                const xAxisColumnDataRowIndex = getDataRowIndex(xAxisColumns[0]);
                xValueRange = columnValueRanges[xAxisColumnDataRowIndex];
                infinityValuePointsX =
                    infinityValuePointForColumn[xAxisColumnDataRowIndex];
            }
            else {
                xValueHash = xAxisValues
                    .map(val => getDisplayValue(val, this.options.getTranslatedText))
                    .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                xRawValues = xAxisValues.map(val => isCustomCalendarTimeSeriesChart
                    ? val
                    : getDisplayValue(val, this.options.getTranslatedText));
                if (!uniqueXAxisValuesHash.hasOwnProperty(xValueHash)) {
                    logger.error('no x value for xAxisValueHashKey', xValueHash);
                }
                xValue = uniqueXAxisValuesHash[xValueHash];
            }
            let yValue = null;
            let yValueHash = null;
            let yValueRange = null;
            let infinityValuePointsY = null;
            const yAxisColumnDataRowIndex = getDataRowIndex(yAxisColumns[0]);
            if (!isYAxisOrdinalBased) {
                yValueHash = yAxisValues[0];
                yValue = yValueHash;
                yRawValues = [yValue];
                yValueRange = columnValueRanges[yAxisColumnDataRowIndex];
                infinityValuePointsY =
                    infinityValuePointForColumn[yAxisColumnDataRowIndex];
            }
            else {
                yValueHash = yAxisValues[0];
                yRawValues = uniqueYAxisValues[yAxisColumnDataRowIndex].map(obj => getDisplayValue(obj.value, this.options.getTranslatedText));
                if (!uniqueYAxisValuesHash[yAxisColumnDataRowIndex].hasOwnProperty(yValueHash)) {
                    logger.error('no y value for yAxisValueHashKey', yValueHash);
                }
                yValue =
                    uniqueYAxisValuesHash[yAxisColumnDataRowIndex][yValueHash];
            }
            let seriesName = null;
            if (plotEachYAsSeries) {
                yAxisColumns.forEach((column, yAxisColumnIndex) => {
                    let yVal = yAxisValues[yAxisColumnIndex];
                    const dataRowIndex = getDataRowIndex(column);
                    if (isEffectivelyNonNumeric(column)) {
                        const uniques = uniqueYAxisValuesHash[yAxisColumnDataRowIndex];
                        if (!uniques.hasOwnProperty(yVal)) {
                            logger.error('no y value for yAxisValueHashKey', yVal);
                        }
                        yVal = uniques[yVal];
                    }
                    const yValuesRange = columnValueRanges[dataRowIndex];
                    const point = this.getChartPointFromDataPoint(xValue, yVal, dataRow, isXAxisOrdinalBased, isEffectivelyNonNumeric(column), categoryColumn, radialColumn, xValueRange, yValuesRange, infinityValuePointsX, infinityValuePointForColumn[dataRowIndex], xRawValues, yRawValues, legendValues, tooltipDataContent);
                    if (point === null) {
                        return;
                    }
                    seriesName = this.distinctYAxisColumnNames[yAxisColumnIndex];
                    series[seriesName].push(point);
                });
            }
            else {
                const legendValue = legendValues.join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                seriesName = legendValue;
                if (!series.hasOwnProperty(legendValue)) {
                    series[legendValue] = [];
                }
                const point = this.getChartPointFromDataPoint(xValue, yValue, dataRow, isXAxisOrdinalBased, isYAxisOrdinalBased, categoryColumn, radialColumn, xValueRange, yValueRange, infinityValuePointsX, infinityValuePointsY, xRawValues, yRawValues, legendValues, tooltipDataContent);
                if (point === null) {
                    return;
                }
                series[legendValue].push(point);
            }
        });
        let maxRange = Number.NEGATIVE_INFINITY;
        let minRange = Number.POSITIVE_INFINITY;
        const showYAxisLinkingOption = configOptions(getChartType(chartModel), chartModel).allowedConfigurations.showYAxisLinkingOption;
        const isYAxisShared = !showYAxisLinkingOption;
        let sharedInfinityPlaceHolder;
        if (isYAxisShared) {
            Object.keys(columnValueRanges).forEach(dataRowIndex => {
                const colValRange = columnValueRanges[dataRowIndex];
                if (colValRange.min < minRange) {
                    minRange = colValRange.min;
                }
                if (colValRange.max > maxRange) {
                    maxRange = colValRange.max;
                }
            });
            sharedInfinityPlaceHolder = computeAxisInfinityPlaceholderValue(minRange, maxRange);
        }
        Object.keys(columnValueRanges).forEach(dataRowIndex => {
            const colValRange = columnValueRanges[dataRowIndex];
            const infinityValuePoints = infinityValuePointForColumn[dataRowIndex];
            let infinityPlaceholderValue = computeAxisInfinityPlaceholderValue(colValRange.min, colValRange.max);
            let columnHasPositiveInfiniteValues = false;
            let columnHasNegativeInfiniteValues = false;
            if (isYAxisShared) {
                infinityPlaceholderValue = sharedInfinityPlaceHolder;
            }
            infinityValuePoints.forEach(point => {
                ['x', 'y'].forEach(prop => {
                    const val = point[prop];
                    if (isSpecialInfinityValue(val)) {
                        columnHasPositiveInfiniteValues = true;
                        point[prop] = infinityPlaceholderValue;
                    }
                    else if (isSpecialMinusInfinityValue(val)) {
                        columnHasNegativeInfiniteValues = true;
                        point[prop] = -infinityPlaceholderValue;
                    }
                });
            });
            this._columnHasInfinityValues[dataRowIndex] = columnHasPositiveInfiniteValues;
            this._columnHasMinusInfinityValues[dataRowIndex] = columnHasNegativeInfiniteValues;
            if (columnHasPositiveInfiniteValues) {
                this._columnInfinityPlaceholder[dataRowIndex] = infinityPlaceholderValue;
            }
            if (columnHasNegativeInfiniteValues) {
                this._columnNegativeInfinityPlaceholder[dataRowIndex] = -infinityPlaceholderValue;
            }
            _.set(this._maxColumnValues, `${baseQueryIndex}.${dataRowIndex}`, colValRange.max);
            _.set(this._minColumnValues, `${baseQueryIndex}.${dataRowIndex}`, colValRange.min);
            if (radialColumn) {
                const radialDataIndex = getDataRowIndex(radialColumn);
                const minValue = this._maxColumnValues[baseQueryIndex][radialDataIndex];
                const maxValue = this._maxColumnValues[baseQueryIndex][radialDataIndex];
                const infinitePlaceholderValue = computeAxisInfinityPlaceholderValue(minValue, maxValue);
                if (this.columnHasMinusInfinityValues[radialDataIndex] ||
                    this.columnHasInfinityValues[radialDataIndex]) {
                    this._radialInfinityPoints.forEach(p => {
                        if (isSpecialInfinityValue(p.z)) {
                            p.z = infinitePlaceholderValue;
                            this._columnInfinityPlaceholder[radialDataIndex] = infinitePlaceholderValue;
                        }
                        else if (isSpecialMinusInfinityValue(p.z)) {
                            p.z = -infinitePlaceholderValue;
                            this._columnNegativeInfinityPlaceholder[radialDataIndex] = infinitePlaceholderValue;
                        }
                    });
                }
            }
        });
        _.values(series).forEach((points) => {
            points.sort((point1, point2) => {
                return point1.x - point2.x;
            });
        });
        let orderedSeriesNames = [];
        const seriesNameToYAxisIndex = {};
        if (!plotEachYAsSeries) {
            const prevUniqueLegendValues = uniqueLegendValues;
            const prevSeries = series;
            uniqueLegendValues = [];
            series = {};
            prevUniqueLegendValues.forEach(valueObjects => {
                const unformattedSeriesName = valueObjects
                    .map(valueObject => getDisplayValue(valueObject.value, this.options.getTranslatedText))
                    .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                const formattedSeriesName = formatCompositeColumnValue(chartModel, valueObjects, this);
                uniqueLegendValues.push(valueObjects);
                series[formattedSeriesName] = prevSeries[unformattedSeriesName];
                seriesNameToId[formattedSeriesName] = formattedSeriesName;
                orderedSeriesNames.push(formattedSeriesName);
                seriesNameToYAxisIndex[formattedSeriesName] = 0;
            });
            if (getEnvFlags().isCustomSortingEnabled &&
                isCustomSortSupportedForChartType(getChartType(chartModel))) {
                let columnIdsInSequenceWithData = ((_e = (_d = chartModel.data) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.columnDataLite.map(column => column.columnId)) || [];
                columnIdsInSequenceWithData = filterColumnIdsForSeries(uniqueLegendValues, columnIdsInSequenceWithData);
                orderedSeriesNames = customSortOnSeriesData(orderedSeriesNames, getCustomOrderInSequenceWithoutColumnId(columnIdsInSequenceWithData, customSortOrder, uniqueLegendValues), getColumnSortPriorityOrder(chartModel.sortInfo, columnIdsInSequenceWithData, getEnvFlags().isSortV2Enabled
                    ? chartModel.config.axisConfig[0].color
                    : null));
            }
        }
        else {
            orderedSeriesNames = Object.keys(series);
            orderedSeriesNames.forEach((seriesName, yAxisColumnIndex) => {
                seriesNameToYAxisIndex[seriesName] = yAxisColumnIndex;
                seriesNameToId[seriesName] = getId(yAxisColumns[yAxisColumnIndex]);
            });
            orderedSeriesNames.sort();
        }
        const normalizedSeriesNameGetter = this.getNormalizedSeriesName.bind(this);
        const seriesColors = getSeriesColorsMap(chartModel);
        this._xValueToRawValues = uniqueXAxisValues;
        this._yValueToRawValues = uniqueYAxisValues;
        if (isLineStackedChart) {
            const queryIndex = 1;
            const lineStackAugmentQuery = this.getQueryDefinitions()[queryIndex];
            const tooltipColumnIds = isTooltipCustomizationEnabled
                ? ((_f = getTooltipConfig(chartModel)) === null || _f === void 0 ? void 0 : _f.columnIds) || []
                : [];
            const conditionalFormattingColumnIds = isEnhancedCfEnabled
                ? getColumnsUsedInConditionalFormatting(chartModel)
                : [];
            const lineStackTooltipColumns = getTooltipColumns(this.getQueryDefinitions(), [...tooltipColumnIds, ...conditionalFormattingColumnIds], chartModel.columns, queryIndex);
            const augmentQueryData = this.getDataArray()[queryIndex];
            const augmentSeries = {};
            const yAxisColumnsPlottedAsSeries = lineStackAugmentQuery.yAxisColumns;
            const xAxisColumnsInAugmentQuery = lineStackAugmentQuery.xAxisColumns;
            yAxisColumnsPlottedAsSeries.forEach((col, index) => {
                const seriesName = getName(col, false);
                augmentSeries[seriesName] = [];
                seriesNameToId[seriesName] = col.id;
                orderedSeriesNames.push(seriesName);
                seriesNameToYAxisIndex[seriesName] = index + 1;
            });
            const xAxisColumnsDataRowIndices = new Set(xAxisColumnsInAugmentQuery.map(column => {
                return getDataRowIndex(column);
            }));
            const isTimeSeries = getIsTimeSeries(chartModel);
            const yAxisRawValue = [];
            augmentQueryData.getData().forEach((dataRow, pos) => {
                const xValues = dataRow.filter((value, index) => {
                    return xAxisColumnsDataRowIndices.has(index);
                });
                const tooltipDataContent = getTooltipDataContent(lineStackTooltipColumns, chartModel, this, dataRow, pos);
                const xValueHash = isTimeSeries
                    ? getDisplayValue(xValues[0], this.options.getTranslatedText)
                    : xValues
                        .map(val => getDisplayValue(val, this.options.getTranslatedText))
                        .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
                yAxisColumnsPlottedAsSeries.forEach(yAsLineColumn => {
                    const dataRowIndex = getDataRowIndex(yAsLineColumn);
                    const yValue = dataRow[dataRowIndex];
                    const xValue = isTimeSeries
                        ? xValues[0]
                        : uniqueXAxisValuesHash[xValueHash];
                    const seriesName = getName(yAsLineColumn, false);
                    const point = {
                        x: xValue,
                        y: yValue,
                        xRawValues: xValues,
                        yRawValues: [yValue],
                        colorRawValues: [],
                        tooltipDataContent,
                    };
                    const minMaxValuesPath = `${queryIndex}.${dataRowIndex}`;
                    const maxValue = Math.max(_.get(this._maxColumnValues, minMaxValuesPath, Number.NEGATIVE_INFINITY), yValue);
                    const minValue = Math.min(_.get(this._minColumnValues, minMaxValuesPath, Number.POSITIVE_INFINITY), yValue);
                    _.set(this._maxColumnValues, minMaxValuesPath, maxValue);
                    _.set(this._minColumnValues, minMaxValuesPath, minValue);
                    if (!_.isNil(uniqueXAxisValuesHash[xValueHash])) {
                        augmentSeries[seriesName].push(point);
                        if (!yAxisRawValue[dataRowIndex]) {
                            yAxisRawValue[dataRowIndex] = [];
                        }
                        yAxisRawValue[dataRowIndex].push({
                            value: point.yRawValues[0],
                        });
                    }
                });
            });
            yAxisColumnsPlottedAsSeries.forEach(yAsLineColumn => {
                const seriesName = getName(yAsLineColumn, false);
                augmentSeries[seriesName].sort((p1, p2) => {
                    if (isXAxisOrdinalBased) {
                        return p1.x - p2.x;
                    }
                    return (uniqueXAxisValuesHash[getDisplayValue(p1.x, this.options.getTranslatedText)] -
                        uniqueXAxisValuesHash[getDisplayValue(p2.x, this.options.getTranslatedText)]);
                });
            });
            _.assign(series, augmentSeries);
            this._augmentYValueToRawValues = yAxisRawValue;
        }
        this._seriesMap = {};
        const allYAxisColumns = getYAxisColumns(this.chartModel);
        this._series = orderedSeriesNames.map((seriesName, seriesIndex) => {
            var _a, _b;
            const dataPoints = series[seriesName];
            const seriesConfig = {
                data: dataPoints,
            };
            if (seriesName) {
                seriesConfig.name = seriesName;
                seriesConfig.blinkSeriesId = seriesNameToId[seriesName].toLowerCase();
                const yAxisIndex = seriesNameToYAxisIndex[seriesName];
                seriesConfig.valueColumnIdentifier = getId(allYAxisColumns[yAxisIndex]);
            }
            else {
                seriesConfig.name = getName(allYAxisColumns[seriesIndex]);
                seriesConfig.blinkSeriesId = getId(allYAxisColumns[seriesIndex]).toLowerCase();
                seriesConfig.valueColumnIdentifier = getId(allYAxisColumns[seriesIndex]);
            }
            seriesConfig.categoryColumnId =
                categoryColumn && getId(categoryColumn);
            const name = seriesConfig.name;
            const longestSerieLength = (_b = (_a = this._longestSerieNames[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
            if (name && name.length >= longestSerieLength) {
                this._longestSerieNames.push(name);
            }
            if (plotEachYAsSeries) {
                seriesConfig.yAxis = isYAxisShared
                    ? 0
                    : seriesNameToYAxisIndex[seriesName];
            }
            if (isLineStackedChart) {
                seriesConfig.yAxis = isYAxisShared
                    ? 0
                    : seriesNameToYAxisIndex[seriesName];
            }
            const seriesColor = getColorBySerieName(seriesColors, seriesConfig.blinkSeriesId);
            if (!_.isNil(seriesColor)) {
                seriesConfig.color = seriesColor;
            }
            this._seriesMap[seriesConfig.name] = seriesConfig;
            return seriesConfig;
        }, this);
        this._series = configureSeriesVisibility(chartModel, this, this._series);
        this.configurePointRangeInSeries(chartModel);
        const isTimeSeries = getIsTimeSeries(chartModel);
        if (!isXAxisOrdinalBased &&
            isTimeSeries &&
            getEnvFlags().isHandleMissingValuesEnabled) {
            this._series = convertNullToShiftedDateTime(this._series, uniqueXAxisValues, chartModel);
            if (this._xValueToRawValues[0][0].value === null &&
                this._xValueToRawValues[0][0].value !==
                    this._series[0].data[0].x) {
                this._xValueToRawValues[0][0].customValue = this._series[0].data[0].x;
            }
        }
        if (plotEachYAsSeries) {
            this._series = this._series.reduce((acc, serie) => {
                const newIdx = seriesNameToYAxisIndex[serie.name];
                acc[newIdx] = serie;
                return acc;
            }, []);
        }
        this._compositeLegendValueToRawValues = {};
        this._uniqueLegendValues = [];
        uniqueLegendValues.forEach(valueObjects => {
            const formattedValue = formatCompositeColumnValue(chartModel, valueObjects, this);
            this._compositeLegendValueToRawValues[formattedValue.toLowerCase()] = valueObjects.map(valueObject => {
                return valueObject.value;
            });
            this._uniqueLegendValues.push(formattedValue);
        }, this);
        _.remove(this._series, serie => {
            return serie.data && serie.data.length === 0;
        });
        this._hasNoData = !this._series.some(serie => {
            return serie.data && serie.data.length > 0;
        });
    }
    shouldNormalizeBackendData() {
        if (!!this.data && !this.data.isNormalized) {
            return true;
        }
        return false;
    }
    normalizeBackendData(tooltipColumns) {
        if (!this.shouldNormalizeBackendData()) {
            return;
        }
        const dataArray = this.getDataArray();
        const queryDefinitions = this.getQueryDefinitions();
        queryDefinitions.forEach((queryDefinition, index) => {
            const queryData = dataArray[index];
            const data = queryData.getData();
            const totalRows = data.length;
            let column;
            let i;
            let j;
            const columns = _.uniqBy([...queryDefinition.getColumnsInDataOrder(), ...tooltipColumns], 'id');
            const numericColumns = [];
            const nonNumericColumns = [];
            columns.forEach(col => {
                isEffectivelyNumeric(col)
                    ? numericColumns.push(col)
                    : nonNumericColumns.push(col);
            });
            const normalize = (col, currentRow) => {
                const dataValue = currentRow[getDataRowIndex(col)];
                const value = convertValueFromBackend(col, dataValue);
                currentRow[getDataRowIndex(col)] = value;
                const colId = col.id;
                this.displayToCustomCalendarValueMap[colId] =
                    this.displayToCustomCalendarValueMap[colId] || {};
                this.displayToCustomCalendarValueMap[colId][value] = dataValue;
            };
            for (i = 0; i < totalRows; ++i) {
                const currentRow = data[i];
                if (currentRow && currentRow.length > 0) {
                    for (j = 0; j < numericColumns.length; ++j) {
                        normalize(numericColumns[j], currentRow);
                    }
                    for (j = 0; j < nonNumericColumns.length; ++j) {
                        normalize(nonNumericColumns[j], currentRow);
                    }
                }
            }
        });
        this.data.isNormalized = true;
    }
    removeNulls(chartModel) {
        const sortedOnX = isPrimarySortOnXAxisColumns(chartModel, getXAxisColumns(chartModel), getYAxisColumns(chartModel));
        const protectedDataRows = new Set();
        const queryDefinitions = this.getQueryDefinitions();
        const getXValuesHash = (xAxisIndices, dataRow) => {
            const getNestedValues = value => _.flatMap(value, v => _.isObject(v) ? Object.values(v) : v).join('');
            const xValuesHash = xAxisIndices.reduce((hash, index) => {
                const value = dataRow[index];
                const valueToAdd = _.isObject(value)
                    ? getNestedValues(value)
                    : value;
                return hash + valueToAdd;
            }, '');
            return xValuesHash;
        };
        queryDefinitions.forEach((queryDefinition, queryIndex) => {
            const dataArray = this.getDataArray()[queryIndex].getData();
            const columnsByType = queryDefinition.getColumnsGroupedByType();
            const xAxisIndicesInQuery = _.map(columnsByType.x, dataRowIndexGetter);
            const measureIndicesInQuery = _.map(columnsByType.y, dataRowIndexGetter);
            dataArray.forEach(dataRow => {
                const xValuesHash = getXValuesHash(xAxisIndicesInQuery, dataRow);
                const hasValidMeasure = measureIndicesInQuery.some(index => dataRow[index] !== null);
                if (hasValidMeasure) {
                    protectedDataRows.add(xValuesHash);
                }
            });
        });
        queryDefinitions.forEach((queryDefinition, queryIndex) => {
            const data = this.getDataArray()[queryIndex].getData();
            const allIncludedColumnsByType = queryDefinition.getColumnsGroupedByType();
            const allIncludedColumns = queryDefinition.getColumnsInDataOrder();
            const dataRowIndicesAllowingNulls = getDataRowIndicesAllowingNullsOrZeroes(allIncludedColumns, chartModel, ShowNullAsType.NULL, getEnvFlags().isHandleMissingValuesEnabled, sortedOnX);
            const xAxisIndicesInQuery = _.map(allIncludedColumnsByType.x, dataRowIndexGetter);
            Object.entries(allIncludedColumnsByType).forEach(([columnType, columns]) => {
                if (!(columns === null || columns === void 0 ? void 0 : columns.length))
                    return;
                const allIncludedDataRowIndices = _.map(columns, dataRowIndexGetter);
                _.remove(data, (dataRow) => {
                    const xValuesHash = getXValuesHash(xAxisIndicesInQuery, dataRow);
                    return _.every(allIncludedDataRowIndices, dataRowIndex => {
                        if (dataRowIndex >= dataRow.length)
                            return false;
                        const value = dataRow[dataRowIndex];
                        const isInvalid = value === null &&
                            !dataRowIndicesAllowingNulls.hasOwnProperty(dataRowIndex);
                        if (isInvalid &&
                            columnType === 'y' &&
                            protectedDataRows.has(xValuesHash)) {
                            return false;
                        }
                        return isInvalid;
                    });
                });
            });
        });
    }
    configurePointRangeInSeries(chartModel) {
        const series = this.series;
        if (!series) {
            return;
        }
        if (getIsTimeSeries(chartModel) &&
            !getIsXAxisOrdinalBased(chartModel)) {
            const dateTimeColumn = getXAxisColumns(chartModel)[0];
            const timeBucket = getTimeBucket(dateTimeColumn);
            let minTickInterval = getIntervalMillisForTimeBucket(timeBucket);
            let timeBucketCategory = timeBucketToBucketCategory[timeBucket];
            if (!isDateTimeColumn(dateTimeColumn) &&
                timeBucket === timeBuckets.NO_BUCKET) {
                timeBucketCategory = timeBuckets.DAILY;
                minTickInterval = getIntervalMillisForTimeBucket(timeBuckets.DAILY);
            }
            if (minTickInterval) {
                _.each(series, s => {
                    s.pointRange = minTickInterval;
                    s.colsize = minTickInterval;
                    s.timeBucketCategory = timeBucketCategory;
                    s.isTimeSeries = true;
                });
            }
        }
        else if (getIsXAxisOrdinalBased(chartModel)) {
            series.forEach(s => (s.pointRange = 1));
        }
    }
    get columnHasInfinityValues() {
        return this._columnHasInfinityValues;
    }
    get columnHasMinusInfinityValues() {
        return this._columnHasMinusInfinityValues;
    }
    get maxColumnValues() {
        return this._maxColumnValues;
    }
    get minColumnValues() {
        return this._minColumnValues;
    }
    get xAxisHasNonNullValue() {
        return this._xAxisHasNonNullValue;
    }
    get hasError() {
        return this._hasError;
    }
    get series() {
        return this._series;
    }
    get columnInfinityPlaceholder() {
        return this._columnInfinityPlaceholder;
    }
    get columnNegativeInfinityPlaceholder() {
        return this._columnNegativeInfinityPlaceholder;
    }
    get compositeLegendValueToRawValues() {
        return this._compositeLegendValueToRawValues;
    }
    get uniqueLegendValues() {
        return this._uniqueLegendValues;
    }
    get xValueToRawValues() {
        return this._xValueToRawValues;
    }
    get yValueToRawValues() {
        return this._yValueToRawValues;
    }
    get augmentYValueToRawValues() {
        return this._augmentYValueToRawValues;
    }
    get hasNoData() {
        return this._hasNoData;
    }
    getDataArray() {
        return this.dataArray;
    }
    getQueryDefinitions() {
        return this.queryDefinitions;
    }
}
//# sourceMappingURL=highchart-data-model.js.map